import React, { useEffect, useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  Tooltip, ResponsiveContainer, XAxis, YAxis, CartesianGrid, LabelList,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { to12h } from '../../utils/time';
import { useAuth } from '../../context/AuthContext';

const fmt = n =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);

const STATUS = {
  attended:     { label: 'Atendidas',     color: '#2563eb', bg: '#eff6ff', icon: '✅' },
  confirmed:    { label: 'Confirmadas',   color: '#2563eb', bg: '#eff6ff', icon: '✅' },
  pending:      { label: 'Pendientes',    color: '#f59e0b', bg: '#fffbeb', icon: '⏳' },
  cancelled:    { label: 'Canceladas',    color: '#ef4444', bg: '#fef2f2', icon: '❌' },
  not_attended: { label: 'No asistieron', color: '#6b7280', bg: '#f9fafb', icon: '😔' },
  rescheduled:  { label: 'Reagendadas',   color: '#8b5cf6', bg: '#f5f3ff', icon: '🔄' },
};

const BADGE = {
  attended:     { label: 'Atendida',    bg: '#eff6ff', color: '#1d4ed8' },
  confirmed:    { label: 'Confirmada',  bg: '#eff6ff', color: '#1d4ed8' },
  pending:      { label: 'Pendiente',   bg: '#fffbeb', color: '#b45309' },
  cancelled:    { label: 'Cancelada',   bg: '#fef2f2', color: '#b91c1c' },
  not_attended: { label: 'No asistió',  bg: '#f9fafb', color: '#4b5563' },
  rescheduled:  { label: 'Reagendada',  bg: '#f5f3ff', color: '#6d28d9' },
};

const SVC_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#8b5cf6', '#ef4444'];

function pctTrend(curr, prev) {
  if (!prev) return null;
  const p = Math.round(((curr - prev) / prev) * 100);
  return { abs: Math.abs(p), up: p >= 0 };
}

/* ── Tarjeta KPI ───────────────────────────────────────────────────────────── */
function KpiCard({ icon, bg, label, value, curr, prev, invertGood }) {
  const t = pctTrend(curr, prev);
  const good = t ? (invertGood ? !t.up : t.up) : null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e8edf3', borderRadius: 14, padding: '16px 18px', display: 'flex', alignItems: 'flex-start', gap: 13 }}>
      <div style={{ width: 46, height: 46, borderRadius: 12, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '1.55rem', fontWeight: 700, lineHeight: 1.1, color: '#0f172a' }}>{value}</div>
        <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 3 }}>{label}</div>
        {t && (
          <div style={{ fontSize: '0.68rem', fontWeight: 600, color: good ? '#16a34a' : '#dc2626', marginTop: 3, display: 'flex', gap: 3, alignItems: 'center' }}>
            {t.up ? '↑' : '↓'} {t.abs}%
            <span style={{ color: '#94a3b8', fontWeight: 400 }}>vs mes anterior</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Tooltip oscuro ────────────────────────────────────────────────────────── */
function DarkTip({ active, payload, label, currency }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', borderRadius: 10, padding: '8px 14px' }}>
      {label && <p style={{ margin: '0 0 3px', fontSize: '0.7rem', color: '#94a3b8' }}>{label}</p>}
      <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: 700, color: '#f1f5f9' }}>
        {currency ? fmt(payload[0].value) : payload[0].value}
      </p>
    </div>
  );
}

/* ── Etiqueta encima de barra ──────────────────────────────────────────────── */
const BarTop = ({ x, y, width, value }) =>
  value ? <text x={x + width / 2} y={y - 5} textAnchor="middle" fontSize={11} fontWeight={600} fill="#64748b">{value}</text> : null;

/* ════════════════════════════════════════════════════════════════════════════ */
export default function DashboardPage() {
  const { user, hasPlan } = useAuth();
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [finance, setFinance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/reports/dashboard'),
      hasPlan('has_finance')
        ? api.get('/finance/summary').catch(() => ({ data: null }))
        : Promise.resolve({ data: null }),
    ]).then(([d, f]) => { setData(d.data); setFinance(f.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-page"><div className="spinner" /><p>Cargando...</p></div>;

  /* ── Datos derivados ───────────────────────────────────────────────────── */
  const statusList = data?.month_by_status || [];
  const monthTotal     = statusList.reduce((s, r) => s + (r.total || 0), 0);
  const monthAttended  = statusList.find(r => r.estado === 'attended')?.total    || 0;
  const monthCancelled = statusList.find(r => r.estado === 'cancelled')?.total   || 0;
  const monthNoShow    = statusList.find(r => r.estado === 'not_attended')?.total || 0;
  const productivity   = monthTotal > 0 ? Math.round(monthAttended * 100 / monthTotal) : 0;

  const lm                   = data?.last_month || {};
  const lmTotal              = lm.total || 0;
  const lmProductivity       = lmTotal > 0 ? Math.round((lm.attended || 0) * 100 / lmTotal) : 0;

  const ingresos  = finance?.ingresos || finance?.total_ingresos || 0;
  const gastos    = finance?.gastos   || finance?.total_gastos   || 0;
  const utilidad  = ingresos - gastos;
  const lmIncome  = data?.last_month_income || 0;

  const topServMax = Math.max(...(data?.top_services || []).map(s => s.total || 0), 1);

  const weeklyData = (data?.weekly_appointments || []).map(d => {
    const raw = String(d.fecha || '');
    const iso = raw.includes('T') ? raw : raw + 'T12:00:00';
    return { dia: new Date(iso).toLocaleDateString('es-CO', { weekday: 'short' }), Citas: d.total || 0 };
  });

  const pieData = statusList.filter(r => r.total > 0).map(r => ({
    name:  STATUS[r.estado]?.label || r.estado,
    value: r.total,
    color: STATUS[r.estado]?.color || '#94a3b8',
  }));

  const incomeData = (data?.income_trend || []).map(d => {
    const raw = String(d.fecha || '');
    const iso = raw.includes('T') ? raw : raw + 'T12:00:00';
    return { fecha: new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }), Ingresos: d.total || 0 };
  });

  const dayStr = new Date().toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const quickActions = [
    { icon: '📅', label: 'Agendar nueva cita',    path: '/admin/appointments' },
    hasPlan('has_finance') && { icon: '💰', label: 'Registrar venta rápida', path: '/admin/finance' },
    hasPlan('has_reports') && { icon: '📈', label: 'Ver reportes',            path: '/admin/reports' },
    hasPlan('has_workers') && { icon: '👥', label: 'Gestionar trabajadores',  path: '/admin/workers' },
  ].filter(Boolean);

  /* ── Card contenedor ───────────────────────────────────────────────────── */
  const Card = ({ children, style }) => (
    <div style={{ background: '#fff', border: '1px solid #e8edf3', borderRadius: 14, ...style }}>{children}</div>
  );
  const CardHead = ({ title, action }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
      <h3 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 600, color: '#0f172a' }}>{title}</h3>
      {action}
    </div>
  );
  const LinkBtn = ({ label, onClick }) => (
    <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: '0.75rem', fontWeight: 600, padding: 0 }}>
      {label}
    </button>
  );

  return (
    <>
      {/* ── Encabezado ───────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: '#0f172a', textTransform: 'capitalize' }}>
            ¡Bienvenido, {user?.name?.split(' ')[0]}! 👋
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#64748b', textTransform: 'capitalize' }}>{dayStr}</p>
          {user?.plan_name && (
            <span style={{ display: 'inline-block', marginTop: 8, fontSize: '0.72rem', fontWeight: 600, background: '#fef3c7', color: '#b45309', padding: '3px 12px', borderRadius: 99, border: '1px solid #fde68a' }}>
              {user.plan_name}
            </span>
          )}
        </div>
      </div>

      {/* ── KPI 2×3 ──────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(165px, 1fr))', gap: 12, marginBottom: 18 }}>
        <KpiCard icon="📅" bg="#dbeafe" label="Total citas (mes)"   value={data?.month_appointments || 0}
          curr={data?.month_appointments} prev={lmTotal} />
        <KpiCard icon="👤" bg="#dcfce7" label="Clientes totales"    value={data?.total_clients || 0} />
        {hasPlan('has_finance')
          ? <KpiCard icon="💵" bg="#fef9c3" label="Ingresos del mes" value={fmt(ingresos)}
              curr={ingresos} prev={lmIncome} />
          : <KpiCard icon="✅" bg="#fef9c3" label="Atendidas hoy"    value={data?.today?.attended || 0} />}
        <KpiCard icon="📈" bg="#f3e8ff" label="Productividad"       value={`${productivity}%`}
          curr={productivity} prev={lmProductivity} />
        <KpiCard icon="❌" bg="#fee2e2" label="Cancelaciones (mes)"  value={monthCancelled}
          curr={monthCancelled} prev={lm.cancelled} invertGood />
        <KpiCard icon="😔" bg="#f1f5f9" label="No asistieron (mes)"  value={monthNoShow}
          curr={monthNoShow} prev={lm.not_attended} invertGood />
      </div>

      {/* ── Gráfica semanal + Dona ────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 14, marginBottom: 14 }}>

        {/* Barras — citas por día */}
        <Card style={{ padding: '20px 20px 10px' }}>
          <CardHead title="Citas por día (esta semana)"
            action={<LinkBtn label="Ver calendario →" onClick={() => navigate('/admin/calendar')} />} />
          {weeklyData.length === 0
            ? <p style={{ color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center', padding: '40px 0' }}>Sin datos esta semana</p>
            : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={weeklyData} margin={{ top: 24, right: 8, left: -28, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="dia" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<DarkTip />} cursor={{ fill: '#f8fafc' }} />
                  <Bar dataKey="Citas" fill="#2563eb" radius={[6, 6, 0, 0]} maxBarSize={46}>
                    <LabelList dataKey="Citas" content={BarTop} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
        </Card>

        {/* Dona — estado citas */}
        <Card style={{ padding: '20px' }}>
          <CardHead title="Estado de las citas" />
          {pieData.length === 0
            ? <p style={{ color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center', padding: '40px 0' }}>Sin citas este mes</p>
            : (
              <>
                <div style={{ position: 'relative', marginBottom: 12 }}>
                  <ResponsiveContainer width="100%" height={148}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={44} outerRadius={68}
                        paddingAngle={2} dataKey="value" startAngle={90} endAngle={-270}>
                        {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Centro de la dona */}
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', pointerEvents: 'none' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>{monthTotal}</div>
                    <div style={{ fontSize: '0.62rem', color: '#94a3b8', marginTop: 1 }}>Total</div>
                  </div>
                </div>
                {/* Leyenda */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {pieData.map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.75rem' }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                      <span style={{ flex: 1, color: '#475569' }}>{d.name}</span>
                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{d.value}</span>
                      <span style={{ color: '#94a3b8', minWidth: 34, textAlign: 'right' }}>
                        {monthTotal > 0 ? Math.round(d.value * 100 / monthTotal) : 0}%
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
        </Card>
      </div>

      {/* ── Servicios + Trabajadores ──────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: hasPlan('has_workers') ? '1fr 1fr' : '1fr', gap: 14, marginBottom: 14 }}>

        {/* Servicios más vendidos */}
        <Card style={{ padding: '20px' }}>
          <CardHead title="Servicios más vendidos"
            action={hasPlan('has_reports') && <LinkBtn label="Ver reporte completo →" onClick={() => navigate('/admin/reports')} />} />
          {(data?.top_services || []).length === 0
            ? <p style={{ color: '#94a3b8', fontSize: '0.82rem' }}>Sin datos este mes</p>
            : (data.top_services).map((s, i) => {
                const pct = Math.round((s.total || 0) * 100 / topServMax);
                return (
                  <div key={i} style={{ marginBottom: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ fontSize: '0.83rem', fontWeight: 500, color: '#1e293b' }}>{s.nombre || s.name}</span>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: SVC_COLORS[i] }}>{pct}%</span>
                    </div>
                    <div style={{ height: 8, background: '#f1f5f9', borderRadius: 99 }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: SVC_COLORS[i], borderRadius: 99, transition: 'width .6s' }} />
                    </div>
                  </div>
                );
              })}
        </Card>

        {/* Top trabajadores */}
        {hasPlan('has_workers') && (
          <Card style={{ padding: '20px' }}>
            <CardHead title="Top trabajadores (esta semana)"
              action={<LinkBtn label="Ver todos" onClick={() => navigate('/admin/workers')} />} />
            {(data?.top_workers || []).length === 0
              ? <p style={{ color: '#94a3b8', fontSize: '0.82rem' }}>Sin actividad esta semana</p>
              : (data.top_workers).map((w, i) => {
                  const medals   = ['🥇', '🥈', '🥉'];
                  const avBg     = ['#dbeafe', '#dcfce7', '#fef9c3'][i] || '#f1f5f9';
                  const avFg     = ['#1d4ed8', '#15803d', '#854d0e'][i] || '#475569';
                  const barColor = w.eficiencia >= 80 ? '#16a34a' : w.eficiencia >= 60 ? '#f59e0b' : '#ef4444';
                  const initial  = (w.barbero || '?').charAt(0).toUpperCase();
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 16 }}>
                      <span style={{ fontSize: '1.25rem', lineHeight: 1, flexShrink: 0 }}>{medals[i]}</span>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: avBg, color: avFg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 700, flexShrink: 0 }}>
                        {initial}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                          <div>
                            <div style={{ fontSize: '0.83rem', fontWeight: 600, color: '#1e293b' }}>{w.barbero}</div>
                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{w.total_citas} servicios · {fmt(w.ingresos)}</div>
                          </div>
                          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: barColor }}>{w.eficiencia}%</span>
                        </div>
                        <div style={{ height: 5, background: '#f1f5f9', borderRadius: 99 }}>
                          <div style={{ width: `${w.eficiencia}%`, height: '100%', background: barColor, borderRadius: 99 }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
          </Card>
        )}
      </div>

      {/* ── Agenda de hoy ────────────────────────────────────────────────────── */}
      <Card style={{ padding: '20px', marginBottom: 14 }}>
        <CardHead title="Agenda de hoy"
          action={<LinkBtn label="Ver calendario →" onClick={() => navigate('/admin/calendar')} />} />
        {(data?.today_agenda || []).length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: '0.82rem' }}>
            No hay citas para hoy.{' '}
            <span style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate('/admin/appointments')}>
              Agendar una →
            </span>
          </p>
        ) : (data.today_agenda).map((a, i, arr) => {
          const bm  = BADGE[a.estado] || { label: a.estado, bg: '#f1f5f9', color: '#475569' };
          const ini = (a.cliente || '?').charAt(0).toUpperCase();
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < arr.length - 1 ? '1px solid #f8fafc' : 'none' }}>
              <span style={{ fontSize: '0.78rem', color: '#64748b', minWidth: 50, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {a.hora_inicio ? to12h(a.hora_inicio) : '--:--'}
              </span>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#dbeafe', color: '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.82rem', fontWeight: 700, flexShrink: 0 }}>
                {ini}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.84rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.cliente}</div>
                <div style={{ fontSize: '0.72rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.servicio}{a.barbero ? ` · ${a.barbero}` : ''}
                </div>
              </div>
              <span style={{ fontSize: '0.7rem', padding: '4px 11px', borderRadius: 99, background: bm.bg, color: bm.color, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {bm.label}
              </span>
            </div>
          );
        })}
      </Card>

      {/* ── Ingresos 30 días (solo con plan finance) ──────────────────────────── */}
      {hasPlan('has_finance') && incomeData.length > 0 && (
        <Card style={{ padding: '20px', marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 600, color: '#0f172a' }}>Ingresos (últimos 30 días)</h3>
              <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: '#64748b' }}>Total ingresos</p>
            </div>
            <span style={{ fontSize: '1.3rem', fontWeight: 700, color: '#b45309' }}>{fmt(ingresos)}</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={incomeData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="ig" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#2563eb" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0}   />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="fecha" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={4} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                tickFormatter={v => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : v >= 1000 ? `$${(v/1000).toFixed(0)}K` : `$${v}`} />
              <Tooltip content={<DarkTip currency />} />
              <Area type="monotone" dataKey="Ingresos" stroke="#2563eb" strokeWidth={2.5} fill="url(#ig)" dot={false} activeDot={{ r: 5, fill: '#2563eb' }} />
            </AreaChart>
          </ResponsiveContainer>
          {/* Resumen financiero */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginTop: 16, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
            {[
              { label: 'Ingresos', value: fmt(ingresos), color: '#16a34a' },
              { label: 'Gastos',   value: fmt(gastos),   color: '#ef4444' },
              { label: 'Utilidad', value: fmt(utilidad), color: utilidad >= 0 ? '#16a34a' : '#ef4444' },
            ].map((f, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: f.color }}>{f.value}</div>
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 2 }}>{f.label}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Citas por estado (este mes) ───────────────────────────────────────── */}
      {statusList.length > 0 && (
        <Card style={{ padding: '20px', marginBottom: 14 }}>
          <CardHead title="Citas por estado (este mes)"
            action={hasPlan('has_reports') && <LinkBtn label="Ver reporte detallado →" onClick={() => navigate('/admin/reports')} />} />
          <div>
            {statusList.map((r, i) => {
              const s = STATUS[r.estado];
              if (!s) return null;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: i < statusList.length - 1 ? '1px solid #f8fafc' : 'none' }}>
                  <span style={{ width: 30, height: 30, borderRadius: 8, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', flexShrink: 0 }}>
                    {s.icon}
                  </span>
                  <span style={{ flex: 1, fontSize: '0.84rem', color: '#475569' }}>{s.label}</span>
                  <span style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>{r.total}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── Acciones rápidas ──────────────────────────────────────────────────── */}
      <Card style={{ padding: '20px' }}>
        <h3 style={{ margin: '0 0 14px', fontSize: '0.92rem', fontWeight: 600, color: '#0f172a' }}>Acciones rápidas</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
          {quickActions.map((a, i) => (
            <button key={i} onClick={() => navigate(a.path)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, cursor: 'pointer', textAlign: 'left', transition: 'all .15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>
              <span style={{ fontSize: '1.15rem' }}>{a.icon}</span>
              <span style={{ fontSize: '0.78rem', fontWeight: 500, color: '#1e293b', lineHeight: 1.3, flex: 1 }}>{a.label}</span>
              <span style={{ color: '#94a3b8', fontSize: '1rem' }}>›</span>
            </button>
          ))}
        </div>
      </Card>
    </>
  );
}
